// ============================================================
// نمایش موقعیت + آدرس — بدون سرویس کلیددار
//
// نام فایل از نسخه‌ی اولش مانده (نقشه‌ی استاتیک). حالا دیگر تصویر
// استاتیک نیست: یک نقشه‌ی واقعی Leaflet است با کاشی‌های رایگان، و
// آدرس هم از Nominatim می‌آید نه از سرویس reverse نشان.
//
// prop قدیمی apiKey دیگر لازم نیست ولی اختیاری نگه داشته شده تا
// فراخوان‌های قبلی نشکنند؛ نادیده گرفته می‌شود.
// ============================================================

import React, { useEffect, useState } from "react";
import { MapContainer, Marker, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Loader2, MapPin } from "lucide-react";

import MapAutoSize from "./map/MapAutoSize";
import TileLayerWithFallback from "./map/TileLayerWithFallback";
import { reverseGeocode } from "./map/geocode";
import { MAX_ZOOM, pulsePin, useIsDarkMode } from "./map/tiles";

interface StaticMapMarkerProps {
    lat: number;
    lng: number;
    zoom?: number;
    /** فقط ارتفاع به کار می‌آید؛ عرض همیشه تمام ظرف است. */
    width?: number;
    height?: number;
    /** @deprecated دیگر لازم نیست — نقشه و آدرس هر دو بی‌کلیدند. */
    apiKey?: string;
}

/** با عوض شدن مختصات، نقشه روی نقطه‌ی تازه می‌رود. */
const FollowPoint: React.FC<{ lat: number; lng: number; zoom: number }> = ({ lat, lng, zoom }) => {
    const map = useMap();
    useEffect(() => {
        map.setView([lat, lng], zoom);
    }, [map, lat, lng, zoom]);
    return null;
};

const StaticMapMarker: React.FC<StaticMapMarkerProps> = ({
    lat,
    lng,
    zoom = 17,
    height = 300,
}) => {
    const dark = useIsDarkMode();
    const [address, setAddress] = useState("");
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setFailed(false);
        setAddress("");

        reverseGeocode(lat, lng)
            .then((found) => {
                if (!alive) return;
                setAddress(found);
            })
            .catch(() => {
                if (alive) setFailed(true);
            })
            .finally(() => {
                if (alive) setLoading(false);
            });

        return () => {
            alive = false;
        };
    }, [lat, lng]);

    return (
        <div className="w-full overflow-hidden border rounded-lg dark:border-gray-700">
            <MapContainer
                center={[lat, lng]}
                zoom={zoom}
                maxZoom={MAX_ZOOM}
                scrollWheelZoom={false}
                // ردگیری resize به MapAutoSize سپرده شده — توضیحش آنجاست
                trackResize={false}
                style={{ height, width: "100%", zIndex: 1 }}
            >
                <TileLayerWithFallback dark={dark} />
                <Marker position={[lat, lng]} icon={pulsePin()} />
                <MapAutoSize />
                <FollowPoint lat={lat} lng={lng} zoom={zoom} />
            </MapContainer>

            <div className="flex flex-col p-2 text-xs bg-gray-50 dark:bg-gray-900">
                <div className="flex items-center gap-1 mb-1">
                    <MapPin size={12} className="text-red-500" />
                    <span className="font-medium">مختصات:</span>
                    <span dir="ltr">
                        {lat.toFixed(6)}، {lng.toFixed(6)}
                    </span>
                </div>

                {loading && (
                    <div className="flex items-center gap-1 text-gray-500">
                        <Loader2 size={12} className="animate-spin" />
                        <span>دریافت آدرس...</span>
                    </div>
                )}

                {!loading && address && (
                    <div className="flex items-start gap-1 text-gray-600 dark:text-gray-400">
                        <MapPin size={12} className="text-blue-500 mt-0.5 flex-shrink-0" />
                        <span dir="rtl" className="text-right">
                            {address}
                        </span>
                    </div>
                )}

                {!loading && !address && !failed && (
                    <div className="text-gray-400">آدرسی برای این نقطه ثبت نشده است.</div>
                )}

                {!loading && failed && (
                    <div className="text-xs text-red-500">
                        امکان دریافت آدرس وجود ندارد. لطفاً اتصال اینترنت را بررسی کنید.
                    </div>
                )}
            </div>
        </div>
    );
};

export default StaticMapMarker;
